import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { type FC, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { z } from 'zod';

import BulletsTitle from '@/components/BulletPoints/BulletsTitle';
import HighlightText from '@/components/BulletPoints/HighlightText';
import Button from '@/components/Button';
import Text from '@/components/Text';
import { queries } from '@/queries';
import type { BulletPointsType } from '@/services/bullet-points';
import type { DjangoData } from '@/services/common';
import type { CompanyProfileDetail } from '@/services/external-offers';

import Layout from './Layout';

type Props = {
  initialBulletPointsData: DjangoData<BulletPointsType>;
  companyData: CompanyProfileDetail;
};
const BulletPoints: FC<Props> = ({ companyData, initialBulletPointsData }) => {
  const { query } = useRouter();
  const companyId = z.coerce.number().parse(query.id);
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      ...queries.bulletPoints.list({ companies: companyId }),
      getNextPageParam: (lastPage, allPages) =>
        lastPage.next ? allPages.length : undefined,
      initialData: {
        pageParams: [undefined, 1],
        pages: [initialBulletPointsData],
      },
    });

  const [ref, inView] = useInView();
  useEffect(() => {
    if (inView) fetchNextPage();
  }, [inView]);

  const bulletPointsResults = data?.pages.map(({ results }) => results).flat();
  return (
    <Layout title={`${companyData.name} Bullets`}>
      <div className="flex flex-col gap-10">
        <BulletsTitle />
        <div className="flex flex-col border-t border-jb-neutral-400" />
        <div>
          <Text variant="h5" className="text-jb-neutral-1000">
            🎯 Bullet Points That Land Jobs at {companyData.name}
          </Text>
          <ul className="marker:text-primary-600' flex w-full list-disc flex-col gap-6 p-2 pl-4 marker:text-h4 md:!p-6 md:!pl-10">
            {bulletPointsResults?.map((bullet) => (
              <li className="text-neutral-1000" key={bullet.id}>
                <Text
                  variant="jb-body-large"
                  className="whitespace-pre-wrap !leading-8"
                >
                  <HighlightText bullet={bullet} />
                </Text>
              </li>
            ))}
          </ul>
          {hasNextPage && (
            <div className="flex w-full justify-center">
              <Button
                variant="candidate-white"
                size="sm"
                ref={ref}
                onClick={() => fetchNextPage()}
                isLoading={isFetchingNextPage}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
export default BulletPoints;
